import { IHistory, ConfigField } from '../../../../../types';
import { ApplicationState } from '../../../../../store';
import * as types from 'types';

type Fields = Pick<
  IHistory,
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'previousUkStudyVisas'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason'
>;

type Visible = Record<
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason',
  boolean
>;

type Mandatory = Record<
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason',
  boolean
>;

const toBool = (val?: boolean) => (val === undefined ? true : val);

export function getPageStatus(
  config: ApplicationState['config'],
  fields: Fields
): {
  complete: boolean;
  visible: Visible;
} {
  const {
    lastCourse,
    lastCourseOther,
    lastCourseYear,
    hasOverseasTravel,
    overseasTravelTimes,
    hasUkTravel,
    ukTravelTimes,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisa,
    previousUkStudyVisas,
    lastCourseLevel,
    lowerOrSameLevelReason
  } = fields;

  const {
    LastCourse: lastCourseConfig,
    LastCourseOther: lastCourseOtherConfig,
    LastCourseYear: lastCourseYearConfig,
    HasOverseasTravel: hasOverseasTravelConfig,
    OverseasTravelTimes: overseasTravelTimesConfig,
    HasUKTravel: hasUkTravelConfig,
    UKTravelTimes: ukTravelTimesConfig,
    HasPreviousUkStudy: hasPreviousUkStudyConfig,
    HasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig,
    LastCourseLevel: lastCourseLevelConfig,
    LowerOrSameLevelReason: lowerOrSameLevelReasonConfig
  }: {
    [key: string]: ConfigField;
  } = config.Checklist.History.Page1;

  const visible: Visible = {
    lastCourse: toBool(lastCourseConfig?.visible),
    lastCourseOther: toBool(lastCourseOtherConfig?.visible) && lastCourse === 'Other',
    lastCourseYear: toBool(lastCourseYearConfig?.visible),
    hasOverseasTravel: toBool(hasOverseasTravelConfig?.visible),
    overseasTravelTimes: toBool(overseasTravelTimesConfig?.visible) && hasOverseasTravel === true,
    hasUkTravel: toBool(hasUkTravelConfig?.visible) && hasOverseasTravel === true,
    ukTravelTimes: toBool(ukTravelTimesConfig?.visible) && hasOverseasTravel === true && hasUkTravel === true,
    hasPreviousUkStudy: hasPreviousUkStudyConfig?.visible,
    hasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig?.visible,
    lastCourseLevel: toBool(lastCourseLevelConfig?.visible) && hasPreviousUkStudyVisa === true,
    lowerOrSameLevelReason:
      toBool(lowerOrSameLevelReasonConfig?.visible) &&
      toBool(lastCourseLevelConfig?.visible) &&
      [types.CourseLevel.SAME, types.CourseLevel.LOWER].includes(lastCourseLevel!)
  };

  const mandatory: Mandatory = {
    lastCourse: toBool(lastCourseConfig?.mandatory),
    lastCourseOther: toBool(lastCourseOtherConfig?.mandatory),
    lastCourseYear: toBool(lastCourseYearConfig?.mandatory),
    hasOverseasTravel: toBool(hasOverseasTravelConfig?.mandatory),
    overseasTravelTimes: toBool(overseasTravelTimesConfig?.mandatory),
    hasUkTravel: toBool(hasUkTravelConfig?.mandatory),
    ukTravelTimes: toBool(ukTravelTimesConfig?.mandatory),
    hasPreviousUkStudy: toBool(hasPreviousUkStudyConfig?.mandatory),
    hasPreviousUkStudyVisa: toBool(hasPreviousUkStudyVisaConfig?.mandatory),
    lastCourseLevel: toBool(lastCourseLevelConfig?.mandatory),
    lowerOrSameLevelReason: toBool(lowerOrSameLevelReasonConfig?.mandatory)
  };

  const allVisasComplete = (amount: number): boolean => {
    let complete = (hasPreviousUkStudyVisa === true && amount > 0) || hasPreviousUkStudyVisa === false;
    for (let i = 0; i < amount; i++) {
      complete =
        complete &&
        !!previousUkStudyVisas![i] &&
        previousUkStudyVisas![i].visaType !== '' &&
        previousUkStudyVisas![i].level !== '' &&
        previousUkStudyVisas![i].year !== '' &&
        previousUkStudyVisas![i].isComplete !== undefined &&
        (previousUkStudyVisas![i].isComplete === false
          ? previousUkStudyVisas![i].incompleteReason !== '' &&
            previousUkStudyVisas![i].isIncompleteRecorded !== undefined &&
            previousUkStudyVisas![i].isVisaShortened !== undefined &&
            (previousUkStudyVisas![i].isVisaShortened === true ? previousUkStudyVisas![i].shortenedDate !== '' : true)
          : true);
    }
    return complete;
  };

  // prettier-ignore
  const complete = (!visible.lastCourse || !mandatory.lastCourse || lastCourse !== '')  &&
    (!visible.lastCourseOther || !mandatory.lastCourseOther || lastCourseOther !== '') &&
    (!visible.lastCourseYear || !mandatory.lastCourseYear || lastCourseYear !== '') &&
    (!visible.hasOverseasTravel || !mandatory.hasOverseasTravel || hasOverseasTravel !== undefined) &&
    (!visible.overseasTravelTimes || !mandatory.overseasTravelTimes || overseasTravelTimes !== '') &&
    (!visible.hasUkTravel || !mandatory.hasUkTravel || hasUkTravel !== undefined) &&
    (!visible.ukTravelTimes || !mandatory.ukTravelTimes || ukTravelTimes !== '') &&
    ((!visible.hasPreviousUkStudy || !mandatory.hasPreviousUkStudy || hasPreviousUkStudy === false) ||
    (hasPreviousUkStudy === true && (!visible.hasPreviousUkStudyVisa || !mandatory.hasPreviousUkStudyVisa || hasPreviousUkStudyVisa !== undefined) &&
        (!visible.lastCourseLevel || !mandatory.lastCourseLevel || lastCourseLevel !== undefined) &&
        (!visible.lowerOrSameLevelReason || !mandatory.lowerOrSameLevelReason || (lowerOrSameLevelReason !== undefined && lowerOrSameLevelReason !== '')) &&
    allVisasComplete(Number(previousUkStudyVisas!.length))));

  return {
    complete,
    visible
  };
}
